import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { onSubscribe } from '@rxjs-lifecycle/core';
import { finalize, Observable, of, shareReplay } from 'rxjs';
import { MainMapService } from 'app/shared/components/main-map/main-map.service';
import { LoadingController } from 'app/shared/helpers/loading.controller';
import { ScreenService } from 'app/shared/services/screen.service';
import {
  ExtractExpr,
  ItemExprUtils,
} from 'app/shared/utils/item-expr.utils';
import { EsriUtils } from 'app/shared/utils/esri.utils';

interface ColumnOption {
  name: string;
  label?: string;
}

@Component({
  selector: 'app-select-objects-table',
  templateUrl: 'select-objects-table.component.html',
  styleUrls: ['select-objects-table.component.scss'],
})
export class SelectObjectsTableComponent implements OnChanges {
  @Input()
  resultList: __esri.Graphic[];
  @Input()
  fields: __esri.Field[] = [];

  @Input()
  selectedItemKeys: any[];
  @Output()
  selectedItemKeysChange = new EventEmitter<any[]>();

  @Input()
  showSelectControlsEnabled: boolean;

  @Input()
  keyExpr: ExtractExpr<__esri.Graphic>;

  private elementKeySelectedSet = new Set<any>();

  pageIndex = 0;

  columnsToDisplay: ColumnOption[];
  displayedColumns: string[];

  _pagedData: any[];
  pageSize = 4;

  private _valueColumnsMap = new Map<any, Map<__esri.Field, Observable<any>>>();

  isMobileModeObservable = this.screenService.observeIsMobile();

  constructor(
    private mainMapService: MainMapService,
    private globalLoadingController: LoadingController,
    private screenService: ScreenService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.resultList) {
      this._valueColumnsMap.clear();
      this.onPageIndexChange(0);
    }

    if (changes.selectedItemKeys) {
      this.handleSelectedItems();
    }

    if (changes.fields || changes.showSelectControlsEnabled) {
      this.handleDisplayedColumns();
    }
  }

  onPageIndexChange(pageIndex: number) {
    const lastPageIndex =
      Math.ceil((this.resultList?.length ?? 0) / this.pageSize) - 1 || 0;
    const newPageIndex = Math.min(pageIndex, lastPageIndex);

    const start = newPageIndex * this.pageSize;

    this._pagedData =
      this.resultList
        ?.slice(start, start + this.pageSize)
        ?.map((x) => x.attributes) ?? [];

    this.pageIndex = newPageIndex;
  }

  onBtnZoomToClick(dataIndex: number) {
    const index = this.dataIndexToListIndex(dataIndex);
    const graphic = this.resultList[index];

    this.mainMapService.goTo(graphic);
  }

  extractKey(dataIndex: number) {
    const index = this.dataIndexToListIndex(dataIndex);
    const graphic = this.resultList[index];
    return ItemExprUtils.extractValue(graphic, index, this.keyExpr);
  }

  isElementSelected(dataIndex: number) {
    const key = this.extractKey(dataIndex);
    return this.elementKeySelectedSet.has(key);
  }

  setElementSelected(dataIndex: number, selected: boolean) {
    const key = this.extractKey(dataIndex);
    if (this.elementKeySelectedSet.has(key) && !selected) {
      this.elementKeySelectedSet.delete(key);
    } else if (selected) {
      this.elementKeySelectedSet.add(key);
    }

    this.emitSelectedKeys();
  }

  getValue(field: __esri.Field, attributes: any) {
    let collection = this._valueColumnsMap.get(attributes);
    if (!collection) {
      collection = new Map();
      this._valueColumnsMap.set(attributes, collection);
    }

    let observable = collection.get(field);
    if (!observable) {
      observable = this.handleValue(attributes, field).pipe(
        onSubscribe(() => {
          this.globalLoadingController.show();
        }),
        finalize(() => {
          this.globalLoadingController.hide();
        }),
        shareReplay({
          bufferSize: 1,
          refCount: true,
        })
      );
      collection.set(field, observable);
    }

    return observable;
  }

  private handleDisplayedColumns() {
    this._valueColumnsMap.clear();
    this.displayedColumns = !this.showSelectControlsEnabled ? [] : ['actions'];
    if (this.fields) {
      this.displayedColumns.push(...this.fields.map((x) => x.name));
    }
  }

  private handleValue(attributes: any, field: __esri.Field): Observable<any> {
    let value = attributes[field.name];

    if (field?.domain) {
      value = EsriUtils.getFieldCodedDomainValue(
        value,
        (field.domain as __esri.CodedValueDomain).codedValues
      );
    }

    return of(value);
  }

  private handleSelectedItems() {
    this.elementKeySelectedSet = new Set(this.selectedItemKeys ?? []);
  }

  private dataIndexToListIndex(dataIndex: number) {
    return dataIndex + this.pageIndex * this.pageSize;
  }

  private emitSelectedKeys() {
    this.selectedItemKeys = Array.from(this.elementKeySelectedSet.keys());
    this.selectedItemKeysChange.emit(this.selectedItemKeys);
  }
}
